import { create } from 'zustand';
import { buildApprovalQueue } from './demoData';
import { CONTROL_EVENTS } from '../../../shared/control-contracts.js';
import { getBackendOrigin, resolveApiFetchBase } from '../utils/runtimeConfig';
import { resolveRehydratedGraphState } from '../../../lib/session/rehydrate';
import { sanitizeWorkspaceSessionFields } from '../utils/sessionDisplay';
import { filterLiveConnectedAgents } from '../utils/agentPresence';
import { buildWorkspaceAuthRequestInit } from '../services/sessionRequestAuth';
import { requestUsageRefresh } from '../utils/usageRefreshBridge';

function parseAgentPresenceMs(agent) {
  const raw = agent?.lastSeen || agent?.syncedAt || agent?.connectedAt || agent?.lastSeenAt;
  const t = Date.parse(raw || '');
  return Number.isNaN(t) ? null : t;
}

/**
 * Re-hydrating from the API can carry slightly stale `lastSeen` while the in-memory
 * store still reflects a live attach. Merging preserves the fresher timestamp so
 * navigation (e.g. Settings → Dashboard) does not drop the agent and trip guards.
 */
function mergeConnectedAgentsForHydrate(session, graphState, previousActiveSessionId, existingAgents) {
  const fromGraph = Array.isArray(graphState.connectedAgents) ? graphState.connectedAgents : [];
  const fromSession = Array.isArray(session?.connectedAgents) ? session.connectedAgents : [];
  const serverList = fromGraph.length > 0 ? fromGraph : fromSession;

  const sameSession = Boolean(
    session?.id
    && previousActiveSessionId
    && String(session.id) === String(previousActiveSessionId)
  );

  if (!sameSession || !Array.isArray(existingAgents) || existingAgents.length === 0) {
    return filterLiveConnectedAgents(Array.isArray(serverList) ? serverList : []);
  }

  const byId = new Map();
  for (const agent of serverList) {
    if (agent && agent.id) {
      byId.set(agent.id, { ...agent });
    }
  }

  for (const local of existingAgents) {
    if (!local || !local.id) {
      continue;
    }

    const fromServer = byId.get(local.id) || {};
    const merged = { ...fromServer, ...local };
    const tServer = parseAgentPresenceMs(fromServer);
    const tLocal = parseAgentPresenceMs(local);
    const best = Math.max(tServer ?? 0, tLocal ?? 0);
    if (best > 0) {
      merged.lastSeen = new Date(best).toISOString();
    }
    merged.status = merged.status || local.status || fromServer.status || 'connected';
    byId.set(local.id, merged);
  }

  return filterLiveConnectedAgents([...byId.values()]);
}

function syncSelectedNode(selectedNode, nodes) {
  if (!selectedNode) return null;
  return nodes.find((node) => node.id === selectedNode.id) || null;
}

function updateSessionHeartbeat(activeSession, heartbeat, status = 'live') {
  if (!activeSession) return null;

  return {
    ...activeSession,
    heartbeat,
    status,
    elapsed: 'Just now',
  };
}

function syncSessions(sessions, activeSession) {
  if (!activeSession) return sessions;

  const existing = sessions.some((session) => session.id === activeSession.id);
  if (!existing) return [activeSession, ...sessions];

  return sessions.map((session) =>
    session.id === activeSession.id ? { ...session, ...activeSession } : session
  );
}

function deriveSessionHeartbeat(snapshot) {
  if (!snapshot) return 'Session live';

  if (snapshot.status === 'paused') {
    return 'Execution paused';
  }

  if (snapshot.status === 'awaiting_approval') {
    const approvalNode = snapshot.graph?.nodes?.find((node) => node.state === 'approval');
    return approvalNode ? `Awaiting approval for ${approvalNode.title}` : 'Awaiting approval';
  }

  if (snapshot.status === 'running') {
    const activeNode = snapshot.graph?.nodes?.find((node) => node.id === snapshot.currentNodeId)
      || snapshot.graph?.nodes?.find((node) => node.state === 'active');
    return activeNode ? `Executing ${activeNode.title}` : 'Execution running';
  }

  if (snapshot.status === 'completed') {
    return 'Execution completed';
  }

  if (snapshot.status === 'failed') {
    return 'Execution failed';
  }

  return 'Session ready';
}

function derivePersistedSessionHeartbeat(session, graphState) {
  const approvalNode = graphState.nodes.find((node) => node.state === 'approval');
  if (approvalNode) {
    return `Awaiting approval for ${approvalNode.title}`;
  }

  const activeNode = graphState.nodes.find((node) => node.state === 'active');
  if (activeNode) {
    return `Executing ${activeNode.title}`;
  }

  if (session?.status === 'paused') {
    return 'Execution paused';
  }

  if (graphState.connectedAgents.length > 0 || session?.connectedAgents?.length > 0) {
    return 'Session live';
  }

  return 'Waiting for agent';
}

function toPersistedActiveSession(session, graphState, currentActiveSession) {
  const sanitized = sanitizeWorkspaceSessionFields({
    ...(currentActiveSession || {}),
    ...session,
    task: session.task || graphState.metadata?.task || currentActiveSession?.task,
    name: session.name || currentActiveSession?.name,
  });

  return {
    ...(currentActiveSession || {}),
    ...sanitized,
    id: session.id,
    name: sanitized.name || 'Syniq Session',
    task: sanitized.task || 'Live orchestration',
    heartbeat: session.heartbeat || derivePersistedSessionHeartbeat(session, graphState),
    status: session.status || currentActiveSession?.status || 'active',
    elapsed: 'Just now',
    executionId: graphState.executionId || currentActiveSession?.executionId || null,
    revision: currentActiveSession?.revision || 0,
    currentNodeId: graphState.currentNodeId || session.executionPointer || null,
    pendingNodeId: graphState.pendingNodeId || null,
    blockedSkillIds: Array.isArray(graphState.constraints) ? graphState.constraints : [],
  };
}

function applySessionSnapshotToState(state, snapshot) {
  const nodes = Array.isArray(snapshot?.graph?.nodes) ? snapshot.graph.nodes : [];
  const edges = Array.isArray(snapshot?.graph?.edges) ? snapshot.graph.edges : [];
  const activeSession = {
    ...(state.activeSession || {}),
    id: snapshot.sessionId,
    executionId: snapshot.executionId,
    task: sanitizeWorkspaceSessionFields({ task: snapshot.task }).task,
    status: snapshot.status,
    revision: snapshot.revision,
    currentNodeId: snapshot.currentNodeId,
    pendingNodeId: snapshot.pendingNodeId,
    blockedSkillIds: snapshot.blockedSkillIds || [],
    heartbeat: deriveSessionHeartbeat(snapshot),
    elapsed: 'Just now',
  };

  return {
    nodes,
    edges,
    approvalQueue: buildApprovalQueue(nodes),
    selectedNode: syncSelectedNode(state.selectedNode, nodes),
    activeSession,
    sessions: syncSessions(state.sessions, activeSession),
    isExecuting: snapshot.status === 'running',
  };
}

function findFallbackTargetNode(nodes, sourceNodeId) {
  const sourceIndex = nodes.findIndex((node) => node.id === sourceNodeId);
  const fallbackPattern = /\b(manual|review|fallback|reject|alternate|rerout|human)\b/i;
  const tail = sourceIndex >= 0 ? nodes.slice(sourceIndex + 1) : nodes;

  return tail.find((node) =>
    node.state !== 'completed'
    && node.state !== 'skipped'
    && fallbackPattern.test(`${node.title} ${node.subtitle} ${node.reasoning}`)
  ) || null;
}

function runNodeTransition(state, nodeId, action) {
  const nodes = state.nodes.map((node) => ({ ...node }));
  const edges = state.edges.map((edge) => ({ ...edge }));
  const targetNode = nodes.find((node) => node.id === nodeId);

  if (!targetNode) return {};

  let heartbeat = state.activeSession?.heartbeat || 'Session live';
  let sessionStatus = state.activeSession?.status || 'live';
  let executing = state.isExecuting;

  if (action === 'pause') {
    const nextState = targetNode.state === 'paused' ? 'active' : 'paused';
    targetNode.state = nextState;
    heartbeat =
      nextState === 'paused'
        ? `Paused ${targetNode.title.toLowerCase()}`
        : `Resumed ${targetNode.title.toLowerCase()}`;
    executing = nextState !== 'paused';
  }

  if (nodeId === 'approval-gate') {
    const approvalToCompose = edges.find(
      (edge) => edge.from === 'approval-gate' && edge.to === 'response-compose'
    );
    const approvalToFallback = edges.find(
      (edge) => edge.from === 'approval-gate' && edge.to === 'manual-review'
    );
    const composeToPublish = edges.find(
      (edge) => edge.from === 'response-compose' && edge.to === 'publish-brief'
    );
    const reviewToPublish = edges.find(
      (edge) => edge.from === 'manual-review' && edge.to === 'publish-brief'
    );

    const composeNode = nodes.find((node) => node.id === 'response-compose');
    const publishNode = nodes.find((node) => node.id === 'publish-brief');
    const fallbackNode = nodes.find((node) => node.id === 'manual-review');

    if (action === 'approve') {
      targetNode.state = 'completed';
      if (approvalToCompose) approvalToCompose.state = 'active';
      if (approvalToFallback) approvalToFallback.state = 'rerouted';
      if (composeToPublish) composeToPublish.state = 'queued';
      if (reviewToPublish) reviewToPublish.state = 'inactive';
      if (composeNode) composeNode.state = 'active';
      if (publishNode) publishNode.state = 'queued';
      if (fallbackNode) fallbackNode.state = 'idle';
      heartbeat = 'Composing approved response';
      sessionStatus = 'live';
      executing = true;
    }

    if (action === 'reject' || action === 'reroute') {
      targetNode.state = action === 'reject' ? 'skipped' : 'completed';
      if (approvalToCompose) approvalToCompose.state = 'inactive';
      if (approvalToFallback) approvalToFallback.state = 'active';
      if (composeToPublish) composeToPublish.state = 'inactive';
      if (reviewToPublish) reviewToPublish.state = 'queued';
      if (composeNode) composeNode.state = 'idle';
      if (fallbackNode) fallbackNode.state = 'active';
      if (publishNode) publishNode.state = 'queued';
      heartbeat =
        action === 'reject'
          ? 'Approval rejected, rerouted to fallback review'
          : 'Rerouted through manual review lane';
      sessionStatus = 'live';
      executing = true;
    }
  }

  const approvalQueue = buildApprovalQueue(nodes);
  const activeSession = updateSessionHeartbeat(state.activeSession, heartbeat, sessionStatus);
  const selectedNode = syncSelectedNode(state.selectedNode, nodes);

  return {
    nodes,
    edges,
    approvalQueue,
    selectedNode,
    activeSession,
    sessions: syncSessions(state.sessions, activeSession),
    isExecuting: executing,
  };
}

const useStore = create((set, get) => ({
  user: null,
  isAuthenticated: false,
  setUser: (user) =>
    set({
      user,
      isAuthenticated: !!user,
    }),

  connectedAgents: [],
  addAgent: (agent) =>
    set((state) => {
      const incomingType = String(agent.type || 'Prompt').toUpperCase();
      const exists = state.connectedAgents.some((item) => item.id === agent.id);
      const nextAgent = {
        id: agent.id || `agent-${Date.now()}`,
        name: agent.name || 'Connected Agent',
        type: incomingType,
        status: agent.status || 'connected',
        lastSeen: agent.lastSeen || 'Live now',
      };

      const connectedAgents = exists
        ? state.connectedAgents.map((item) => (item.id === nextAgent.id ? nextAgent : item))
        : [...state.connectedAgents, nextAgent];

      const activeSession = state.activeSession
        ? updateSessionHeartbeat(state.activeSession, `${nextAgent.name} connected`, 'live')
        : null;

      return {
        connectedAgents,
        activeSession,
        sessions: activeSession ? syncSessions(state.sessions, activeSession) : state.sessions,
      };
    }),
  removeAgent: (agentId) =>
    set((state) => ({
      connectedAgents: state.connectedAgents.filter((agent) => agent.id !== agentId),
    })),
  updateAgentStatus: (agentId, status) =>
    set((state) => ({
      connectedAgents: state.connectedAgents.map((agent) =>
        agent.id === agentId ? { ...agent, status, lastSeen: 'Just now' } : agent
      ),
    })),
  replaceConnectedAgents: (connectedAgents) =>
    set({
      connectedAgents: Array.isArray(connectedAgents) ? connectedAgents : [],
    }),
  refreshConnectedAgentsPresence: () =>
    set((state) => {
      if (!Array.isArray(state.connectedAgents) || state.connectedAgents.length === 0) {
        return state;
      }

      const now = new Date().toISOString();
      return {
        connectedAgents: state.connectedAgents.map((agent) => ({
          ...agent,
          status: agent.status || 'connected',
          lastSeen: now,
        })),
      };
    }),

  sessions: [],
  activeSession: null,
  setActiveSession: (session) => {
    set((state) => ({
      activeSession: session,
      sessions: syncSessions(state.sessions, session),
    }));

    const ws = get().ws;
    if (session?.id && ws?.sessionId !== session.id) {
      ws.connect(session.id);
    }
    if (session?.id) {
      get().hydrateSessionState(session.id);
    }
  },
  updateSessionName: (name) =>
    set((state) => {
      if (!state.activeSession) return {};
      const activeSession = sanitizeWorkspaceSessionFields({
        ...state.activeSession,
        name,
      });
      return {
        activeSession,
        sessions: syncSessions(state.sessions, activeSession),
      };
    }),
  addSession: (session) =>
    set((state) => ({
      sessions: session?.id ? syncSessions(state.sessions, session) : state.sessions,
    })),
  updateSession: (sessionId, updates) =>
    set((state) => {
      const sessions = state.sessions.map((session) =>
        session.id === sessionId ? { ...session, ...updates } : session
      );
      const activeSession =
        state.activeSession?.id === sessionId
          ? { ...state.activeSession, ...updates }
          : state.activeSession;

      return {
        sessions,
        activeSession,
      };
    }),

  canvasMode: 'flow',
  setCanvasMode: (mode) => set({ canvasMode: mode }),

  nodes: [],
  edges: [],
  setNodes: (nodes) =>
    set((state) => ({
      nodes,
      selectedNode: syncSelectedNode(state.selectedNode, nodes),
      approvalQueue: buildApprovalQueue(nodes),
    })),
  setEdges: (edges) => set({ edges }),
  addNode: (node) =>
    set((state) => {
      const nodes = [...state.nodes, node];
      return {
        nodes,
        approvalQueue: buildApprovalQueue(nodes),
      };
    }),
  addEdge: (edge) =>
    set((state) => ({
      edges: [...state.edges, edge],
    })),
  updateNode: (nodeId, updates) =>
    set((state) => {
      const nodes = state.nodes.map((node) =>
        node.id === nodeId ? { ...node, ...updates } : node
      );
      return {
        nodes,
        selectedNode: syncSelectedNode(state.selectedNode, nodes),
        approvalQueue: buildApprovalQueue(nodes),
      };
    }),

  isExecuting: false,
  executionTrace: [],
  setIsExecuting: (isExecuting) =>
    set((state) => {
      const heartbeat = isExecuting
        ? state.activeSession?.heartbeat || 'Execution resumed'
        : 'Execution paused';
      const activeSession = updateSessionHeartbeat(state.activeSession, heartbeat, 'live');
      return {
        isExecuting,
        activeSession,
        sessions: syncSessions(state.sessions, activeSession),
      };
    }),
  addTraceStep: (step) =>
    set((state) => ({
      executionTrace: [...state.executionTrace, step],
    })),
  clearTrace: () => set({ executionTrace: [] }),

  selectedNode: null,
  setSelectedNode: (node) =>
    set({
      selectedNode: node,
      rightDrawerOpen: !!node,
    }),
  selectNode: (nodeId) =>
    set((state) => {
      const selectedNode = state.nodes.find((node) => node.id === nodeId) || null;
      return {
        selectedNode,
        rightDrawerOpen: !!selectedNode,
      };
    }),

  rightDrawerOpen: false,
  setRightDrawerOpen: (rightDrawerOpen) => set({ rightDrawerOpen }),

  connectModalOpen: false,
  setConnectModalOpen: (connectModalOpen) => set({ connectModalOpen }),
  socketListenersInitialized: false,

  approvalQueue: [],
  approveNode: (nodeId) => get().apiApproveNode(nodeId),
  rejectNode: (nodeId) => get().apiRejectNode(nodeId),
  rerouteNode: (nodeId, targetNodeId) => get().apiRerouteNode(nodeId, targetNodeId),
  pauseNode: () => (get().isExecuting ? get().apiPauseSession() : get().apiResumeSession()),

  notifications: [],
  addNotification: (notification) => {
    const id = Date.now();
    set((state) => ({
      notifications: [...state.notifications, { ...notification, id }],
    }));
    setTimeout(() => {
      set((state) => ({
        notifications: state.notifications.filter((item) => item.id !== id),
      }));
    }, 4000);
  },
  removeNotification: (id) =>
     set((state) => ({
      notifications: state.notifications.filter((notification) => notification.id !== id),
    })),

  ws: null,
  setWs: (ws) => set({ ws }),

  hydratePersistedSession: (session) =>
    set((state) => {
      if (!session?.id) {
        return {};
      }

      const graphState = resolveRehydratedGraphState(session.graphState || {}, []);
      const nodes = Array.isArray(graphState.nodes) ? graphState.nodes : [];
      const edges = Array.isArray(graphState.edges) ? graphState.edges : [];
      const connectedAgents = mergeConnectedAgentsForHydrate(
        session,
        graphState,
        state.activeSession?.id,
        state.connectedAgents
      );
      const activeSession = toPersistedActiveSession(session, graphState, state.activeSession);
      const selectedNode = syncSelectedNode(state.selectedNode, nodes)
        || nodes.find((node) => node.state === 'approval')
        || nodes.find((node) => node.state === 'active')
        || null;

      return {
        connectedAgents,
        nodes,
        edges,
        approvalQueue: buildApprovalQueue(nodes),
        selectedNode,
        activeSession,
        sessions: syncSessions(state.sessions, activeSession),
        rightDrawerOpen: Boolean(selectedNode),
        canvasMode: graphState.mode || session.mode || 'flow',
        isExecuting: graphState.status === 'running',
      };
    }),
  resetWorkspace: () =>
    set({
      connectedAgents: [],
      sessions: [],
      activeSession: null,
      nodes: [],
      edges: [],
      approvalQueue: [],
      selectedNode: null,
      rightDrawerOpen: false,
      canvasMode: 'flow',
      isExecuting: false,
    }),

  clearAgentAttachment: async (sessionIdOverride = null) => {
    const sessionId = sessionIdOverride || get().activeSession?.id || null;
    if (!sessionId) {
      get().addNotification({
        type: 'error',
        title: 'Cannot clear agent',
        message: 'No workspace session is loaded yet. Refresh the page and try again.',
      });
      return { ok: false };
    }

    try {
      const init = await buildWorkspaceAuthRequestInit();
      const response = await fetch(`${get().backendUrl}/api/session/reset-agents`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          ...init.headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId }),
      });

      let payload = null;
      try {
        payload = await response.json();
      } catch {
        payload = null;
      }

      if (!response.ok) {
        const message = payload?.error?.message
          || payload?.message
          || 'Unable to clear the previous agent. Try again in a moment.';
        get().addNotification({
          type: 'error',
          title: 'Clear failed',
          message,
        });
        return { ok: false, error: message };
      }

      if (payload?.session) {
        get().hydratePersistedSession(payload.session);
      } else {
        set((state) => ({
          connectedAgents: [],
          isExecuting: false,
          nodes: [],
          edges: [],
          approvalQueue: [],
          selectedNode: null,
          rightDrawerOpen: false,
          activeSession: state.activeSession
            ? {
                ...state.activeSession,
                status: 'waiting',
                heartbeat: 'Waiting for agent',
              }
            : null,
          sessions: state.activeSession
            ? syncSessions(state.sessions, {
                ...state.activeSession,
                status: 'waiting',
                heartbeat: 'Waiting for agent',
              })
            : state.sessions,
        }));
      }

      requestUsageRefresh(sessionId);
      return { ok: true, session: payload?.session || null, quotaPreserved: payload?.quotaPreserved !== false };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to clear the previous agent.';
      get().addNotification({
        type: 'error',
        title: 'Clear failed',
        message,
      });
      return { ok: false, error: message };
    }
  },

  usage: {
    steps: 0,
    limit: 100,
  },
  incrementUsage: () =>
    set((state) => ({
      usage: {
        ...state.usage,
        steps: state.usage.steps + 1,
      },
    })),

  backendUrl: resolveApiFetchBase(),
  applySessionSnapshot: (snapshot) =>
    set((state) => applySessionSnapshotToState(state, snapshot)),

  /**
   * Initialize WebSocket event listeners that drive canvas animations from real events.
   * Call once when the app mounts.
   */
  initSocketListeners: () => {
    if (get().socketListenersInitialized) {
      return;
    }

    set({ socketListenersInitialized: true });
    // Dynamic import for ESM (Vite)
    import('../services/socketClient.js').then(({ default: client }) => {
      const state = get();

      // ─── Orchestration Events ─────────────────────────────────────
      const mergeGraphPayload = (incomingNodes, incomingEdges) => {
        if (!Array.isArray(incomingNodes) || incomingNodes.length === 0) {
          return null;
        }

        set((state) => {
          const nodeMap = new Map(state.nodes.map((node) => [node.id, node]));
          for (const node of incomingNodes) {
            nodeMap.set(node.id, node);
          }

          const edgeMap = new Map(state.edges.map((edge) => [edge.id, edge]));
          if (Array.isArray(incomingEdges)) {
            for (const edge of incomingEdges) {
              edgeMap.set(edge.id, edge);
            }
          }

          const nodes = Array.from(nodeMap.values());
          const edges = Array.from(edgeMap.values());
          return {
            nodes,
            edges,
            approvalQueue: buildApprovalQueue(nodes),
            isExecuting: nodes.some((node) => node.state === 'active' || node.state === 'queued'),
          };
        });
        return null;
      };

      client.subscribe('orchestration', 'graph:created', (data) => {
        const incomingNodes = data?.nodes || data?.graph?.nodes;
        const incomingEdges = data?.edges || data?.graph?.edges;
        mergeGraphPayload(incomingNodes, incomingEdges);
      });

      client.subscribe('orchestration', 'node:created', (data) => {
        if (data?.node) {
          mergeGraphPayload([data.node], data.edge ? [data.edge] : []);
        }
      });

      client.subscribe('orchestration', 'node:executing', (data) => {
        set((s) => {
          const nodes = s.nodes.map((n) =>
            n.id === data.nodeId ? { ...n, state: 'active' } : n
          );
          return {
            nodes,
            selectedNode: syncSelectedNode(s.selectedNode, nodes),
            approvalQueue: buildApprovalQueue(nodes),
            activeSession: updateSessionHeartbeat(s.activeSession, `Executing ${data.nodeId}`),
          };
        });
      });

      client.subscribe('orchestration', 'node:completed', (data) => {
        set((s) => {
          const nodes = s.nodes.map((n) =>
            n.id === data.nodeId ? { ...n, state: 'completed' } : n
          );
          return {
            nodes,
            selectedNode: syncSelectedNode(s.selectedNode, nodes),
            approvalQueue: buildApprovalQueue(nodes),
          };
        });
      });

      client.subscribe('orchestration', 'node:awaiting_approval', (data) => {
        set((s) => {
          const nodes = s.nodes.map((n) =>
            n.id === data.nodeId ? { ...n, state: 'approval' } : n
          );
          return {
            nodes,
            selectedNode: syncSelectedNode(s.selectedNode, nodes),
            approvalQueue: buildApprovalQueue(nodes),
            rightDrawerOpen: true,
          };
        });
      });

      client.subscribe('orchestration', 'node:approved', (data) => {
        set((s) => {
          const nodes = s.nodes.map((n) =>
            n.id === data.nodeId ? { ...n, state: 'completed' } : n
          );
          return {
            nodes,
            selectedNode: syncSelectedNode(s.selectedNode, nodes),
            approvalQueue: buildApprovalQueue(nodes),
          };
        });
      });

      client.subscribe('orchestration', 'node:rejected', (data) => {
        set((s) => {
          const nodes = s.nodes.map((n) =>
            n.id === data.nodeId ? { ...n, state: 'skipped' } : n
          );
          return {
            nodes,
            selectedNode: syncSelectedNode(s.selectedNode, nodes),
            approvalQueue: buildApprovalQueue(nodes),
          };
        });
      });

      client.subscribe('orchestration', 'node:error', (data) => {
        set((s) => {
          const nodes = s.nodes.map((n) =>
            n.id === data.nodeId ? { ...n, state: 'error' } : n
          );
          return {
            nodes,
            selectedNode: syncSelectedNode(s.selectedNode, nodes),
            approvalQueue: buildApprovalQueue(nodes),
          };
        });
      });

      client.subscribe('orchestration', 'edge:created', (data) => {
        if (data.edge) {
          set((s) => ({ edges: [...s.edges, data.edge] }));
        }
      });

      client.subscribe('orchestration', 'edge:active', (data) => {
        set((s) => ({
          edges: s.edges.map((e) =>
            e.id === data.edgeId ? { ...e, state: 'active' } : e
          ),
        }));
      });

      client.subscribe('orchestration', 'path:rerouted', (data) => {
        set((s) => {
          const exists = s.edges.some((edge) => edge.id === data.edgeId);
          return {
            edges: exists
              ? s.edges.map((edge) =>
                  edge.id === data.edgeId
                    ? { ...edge, state: 'rerouted' }
                    : edge
                )
              : [
                  ...s.edges,
                  {
                    id: data.edgeId,
                    from: data.from,
                    to: data.to,
                    state: 'rerouted',
                    label: 'reroute',
                    type: 'conditional',
                  },
                ],
          };
        });
      });

      // ─── Session Events ───────────────────────────────────────────
      client.subscribe('session', CONTROL_EVENTS.SESSION_STATE, (data) => {
        if (data?.snapshot) {
          get().applySessionSnapshot(data.snapshot);
        }
      });

      client.subscribe('session', 'session:paused', () => {
        set((s) => ({
          isExecuting: false,
          activeSession: updateSessionHeartbeat(s.activeSession, 'Execution paused'),
        }));
      });

      client.subscribe('session', 'session:resumed', () => {
        set((s) => ({
          isExecuting: true,
          activeSession: updateSessionHeartbeat(s.activeSession, 'Execution resumed'),
        }));
      });

      const handleUsageSocketEvent = (data) => {
        const activeId = get().activeSession?.id;
        const eventSessionId = data?.sessionId || activeId;
        if (!eventSessionId || (activeId && eventSessionId !== activeId)) {
          return;
        }
        requestUsageRefresh(eventSessionId);
      };

      client.subscribe('session', 'limit:usage_updated', handleUsageSocketEvent);
      client.subscribe('orchestration', 'limit:usage_updated', handleUsageSocketEvent);

      client.subscribe('orchestration', 'execution.completed', handleUsageSocketEvent);
      client.subscribe('orchestration', 'execution.failed', handleUsageSocketEvent);

      client.subscribe('session', 'agent:connected', (data) => {
        const activeId = get().activeSession?.id;
        if (!activeId || !data) {
          return;
        }
        const matchesSession = data.sessionId === activeId || data.connectionSessionId === activeId;
        if (!matchesSession) {
          return;
        }
        get().replaceConnectedAgents([]);
        const agentData = {
          id: data.agentId || `agent-${Date.now()}`,
          name: data.agentName || 'Connected Agent',
          type: (data.connectionType || 'prompt').toUpperCase(),
          status: 'connected',
          lastSeen: new Date().toISOString(),
        };
        get().addAgent(agentData);
        requestUsageRefresh(activeId);
      });

      client.subscribe('session', 'agent:disconnected', (data) => {
        const activeId = get().activeSession?.id;
        if (!activeId || data?.sessionId !== activeId) {
          return;
        }
        if (data?.agentId) {
          get().removeAgent(data.agentId);
        } else {
          get().replaceConnectedAgents([]);
        }
      });

      client.subscribe('session', 'session:limit_reached', (data) => {
        get().addNotification({
          title: 'Usage limit reached',
          message: data?.connectionBlockedLimit?.reason || data?.blockedLimit?.reason || data?.message || 'Syniq blocked the next execution because the current usage limit has been reached.',
          type: 'warning',
        });
      });

      client.subscribe('control', CONTROL_EVENTS.SESSION_CONSTRAINED, (data) => {
        get().addNotification({
          title: 'Constraints updated',
          message: `Blocked ${data.blockedSkillIds?.length || 0} skill${data.blockedSkillIds?.length === 1 ? '' : 's'}.`,
          type: 'info',
        });
      });

      client.subscribe('control', CONTROL_EVENTS.CONTROL_ERROR, (data) => {
        get().addNotification({
          title: 'Control action failed',
          message: data?.message || 'The backend rejected the requested control action.',
          type: 'error',
        });
      });

      set({ ws: client });

      const latestSessionId = get().activeSession?.id;
      if (latestSessionId && client.sessionId !== latestSessionId) {
        client.connect(latestSessionId);
      }
    }).catch((err) => {
      console.warn('[Syniq] Socket client import failed:', err.message);
    });
  },

  /**
   * Call backend API for control actions (approve/reject/reroute/pause)
   */
  apiApproveNode: async (nodeId) => {
    const state = get();
    const sessionId = state.activeSession?.id || 'default';
    const url = `${state.backendUrl}/node/${nodeId}/approve`;
    try {
      const response = await fetch(url, {
        ...(await buildWorkspaceAuthRequestInit({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          expectedRevision: state.activeSession?.revision,
        }),
        })),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error?.message || 'Approve request failed');
      }
      if (payload?.snapshot) {
        get().applySessionSnapshot(payload.snapshot);
      }
      return true;
    } catch (_error) {
      get().addNotification({
        title: 'Approve failed',
        message: 'The backend could not approve this node.',
        type: 'error',
      });
      return false;
    }
  },

  apiRejectNode: async (nodeId) => {
    const state = get();
    const sessionId = state.activeSession?.id || 'default';
    const url = `${state.backendUrl}/node/${nodeId}/reject`;
    try {
      const response = await fetch(url, {
        ...(await buildWorkspaceAuthRequestInit({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          expectedRevision: state.activeSession?.revision,
        }),
        })),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error?.message || 'Reject request failed');
      }
      if (payload?.snapshot) {
        get().applySessionSnapshot(payload.snapshot);
      }
      return true;
    } catch (_error) {
      get().addNotification({
        title: 'Reject failed',
        message: 'The backend could not reject this node.',
        type: 'error',
      });
      return false;
    }
  },

  apiRerouteNode: async (nodeId, targetNodeId) => {
    const state = get();
    const sessionId = state.activeSession?.id || 'default';
    const url = `${state.backendUrl}/node/${nodeId}/reroute`;
    const fallbackTargetId = targetNodeId || findFallbackTargetNode(state.nodes, nodeId)?.id;
    try {
      if (!fallbackTargetId) {
        throw new Error('No reroute target is available');
      }

      const response = await fetch(url, {
        ...(await buildWorkspaceAuthRequestInit({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          targetNodeId: fallbackTargetId,
          expectedRevision: state.activeSession?.revision,
        }),
        })),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error?.message || 'Reroute request failed');
      }
      if (payload?.snapshot) {
        get().applySessionSnapshot(payload.snapshot);
      }
      return true;
    } catch (_error) {
      get().addNotification({
        title: 'Reroute failed',
        message: 'The backend could not reroute this flow.',
        type: 'error',
      });
      return false;
    }
  },

  apiPauseSession: async () => {
    const state = get();
    const sessionId = state.activeSession?.id || 'default';
    const url = `${state.backendUrl}/session/${sessionId}/pause`;
    try {
      const response = await fetch(url, {
        ...(await buildWorkspaceAuthRequestInit({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          expectedRevision: state.activeSession?.revision,
        }),
        })),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error?.message || 'Pause request failed');
      }
      if (payload?.snapshot) {
        get().applySessionSnapshot(payload.snapshot);
      }
      return true;
    } catch (_error) {
      get().addNotification({
        title: 'Pause failed',
        message: 'The backend could not pause this session.',
        type: 'error',
      });
      return false;
    }
  },

  apiResumeSession: async () => {
    const state = get();
    const sessionId = state.activeSession?.id || 'default';
    const url = `${state.backendUrl}/session/${sessionId}/resume`;
    try {
      const response = await fetch(url, {
        ...(await buildWorkspaceAuthRequestInit({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          expectedRevision: state.activeSession?.revision,
        }),
        })),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error?.message || 'Resume request failed');
      }
      if (payload?.snapshot) {
        get().applySessionSnapshot(payload.snapshot);
      }
      return true;
    } catch (_error) {
      get().addNotification({
        title: 'Resume failed',
        message: 'The backend could not resume this session.',
        type: 'error',
      });
      return false;
    }
  },

  apiConstrainSession: async (blockedSkillIds) => {
    const state = get();
    const sessionId = state.activeSession?.id || 'default';
    const url = `${state.backendUrl}/session/${sessionId}/constrain`;
    try {
      const response = await fetch(url, {
        ...(await buildWorkspaceAuthRequestInit({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          blockedSkillIds,
          expectedRevision: state.activeSession?.revision,
        }),
        })),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error?.message || 'Constrain request failed');
      }
      if (payload?.snapshot) {
        get().applySessionSnapshot(payload.snapshot);
      }
      return true;
    } catch (_error) {
      get().addNotification({
        title: 'Constraint update failed',
        message: 'The backend could not update blocked skills for this session.',
        type: 'error',
      });
      return false;
    }
  },

  hydrateSessionState: async (sessionId) => {
    const backendUrl = get().backendUrl;
    try {
      const response = await fetch(
        `${backendUrl}/session/${sessionId}/state`,
        await buildWorkspaceAuthRequestInit()
      );
      const payload = await response.json();
      if (!response.ok || !payload?.snapshot) {
        return;
      }

      get().applySessionSnapshot(payload.snapshot);
    } catch (_error) {
      // Keep demo workspace if the backend has no persisted state for this session.
    }
  },

  bootstrapWorkspace: () => {
    const state = get();
    if (!state.activeSession?.id) {
      return;
    }

    if (state.nodes.length || state.edges.length || state.connectedAgents.length) {
      return;
    }

    const activeSession = {
      ...state.activeSession,
      heartbeat: state.activeSession.heartbeat || 'Waiting for agent',
      status: state.activeSession.status || 'waiting',
    };

    set({
      activeSession,
      sessions: syncSessions(state.sessions, activeSession),
      nodes: [],
      edges: [],
      approvalQueue: [],
      selectedNode: null,
      rightDrawerOpen: false,
      canvasMode: 'flow',
      isExecuting: false,
    });
  },
}));

export default useStore;
